import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';
import { downloadLedgerReport } from '../../utils/ledger';
import {
  buildMonthlyTrendFromLedger,
  mapWalletTransactionsToLedger,
} from '../../utils/ledger/mapWalletTransactions';

const LedgerReportButton = ({
  client = null,
  period,
  variant = 'auto',
  className = '',
  /** Raw rows from admin `transactions` table — skips ledger_advances Supabase query */
  sourceTransactions = null,
  clientLookup = {},
  machineLookup = null,
  bookingByRef = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFromLedgerAdvances = async () => {
    let query = supabase
      .from('ledger_advances')
      .select(`
        id, txn_date, receipt_no, type, amount, payment_mode,
        reference, machine_code,
        client:clients(id, name, project_site, status)
      `)
      .gte('txn_date', period.start)
      .lte('txn_date', period.end)
      .order('txn_date', { ascending: true });

    if (client?.id) {
      query = query.eq('client_id', client.id);
    }

    const { data: rawTxns, error: txnErr } = await query;
    if (txnErr) throw txnErr;

    const transactions = (rawTxns || []).map((t) => ({
      txn_date: t.txn_date,
      receipt_no: t.receipt_no,
      type: t.type,
      amount: t.amount,
      payment_mode: t.payment_mode,
      reference: t.reference,
      machine_code: t.machine_code,
      client_id: t.client?.id,
      client_name: t.client?.name,
      project_site: t.client?.project_site,
      client_status: t.client?.status,
    }));

    let historicalTrend = [];
    if (variant === 'premium' || (variant === 'auto' && client?.report_tier === 'premium')) {
      const { data: trendData } = await supabase.rpc('get_advance_monthly_trend', {
        p_client_id: client?.id || null,
        p_months: 7,
      });
      historicalTrend = trendData || [];
    }

    return { transactions, historicalTrend };
  };

  const resolveTransactions = async () => {
    if (Array.isArray(sourceTransactions)) {
      let rows = mapWalletTransactionsToLedger(sourceTransactions, clientLookup, {
        machineLookup,
        bookingByRef,
      });
      if (client?.id) {
        rows = rows.filter((r) => r.client_id === client.id);
      }
      const historicalTrend =
        variant === 'premium' || (variant === 'auto' && client?.report_tier === 'premium')
          ? buildMonthlyTrendFromLedger(rows, 7)
          : [];
      return { transactions: rows, historicalTrend };
    }

    try {
      return await fetchFromLedgerAdvances();
    } catch (err) {
      if (err?.code === '42P01' || /ledger_advances|does not exist/i.test(String(err?.message || ''))) {
        throw new Error('No wallet transactions in this date range.');
      }
      throw err;
    }
  };

  const handleClick = async () => {
    if (!period?.start || !period?.end) {
      setError('Select a date range first.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { transactions, historicalTrend } = await resolveTransactions();

      if (transactions.length === 0) {
        setError('No transactions found for this period.');
        return;
      }

      await downloadLedgerReport({
        client,
        period,
        transactions,
        historicalTrend,
        tier: variant === 'auto' ? null : variant,
      });
    } catch (err) {
      console.error('Ledger generation failed:', err);
      setError(err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const effectiveTier = variant === 'auto' ? (client?.report_tier || 'standard') : variant;
  const tierLabel = effectiveTier === 'premium' ? 'Premium' : 'Standard';

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className="inline-flex min-h-[44px] w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-de-gold/40 bg-gradient-to-r from-de-gold/25 to-de-gold/5 px-4 py-2.5 text-sm font-bold text-de-gold disabled:cursor-wait disabled:opacity-60 sm:w-auto"
      >
        {loading ? (
          <>
            <span
              className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-de-gold/30 border-t-de-gold"
              aria-hidden
            />
            Generating PDF…
          </>
        ) : (
          <>
            {String.fromCodePoint(0x1F4C4)} Download {tierLabel} Advance Ledger
          </>
        )}
      </button>
      {error ? (
        <p className="mt-2 text-xs text-red-400">{error}</p>
      ) : (
        <p className="mt-1.5 text-[10px] text-de-muted">
          Tier PDF (Standard 2-page / Premium 4-page) · uses filtered wallet transactions
        </p>
      )}
    </div>
  );
};

export default LedgerReportButton;
